import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

export interface ChartDataItem {
  label: string;
  value: number;
  color: string;
}

export interface DataDrivenChartsProps {
  title?: string;
  subtitle?: string;
  data?: ChartDataItem[];
  background?: string;
}

const defaultData: ChartDataItem[] = [
  { label: 'Mon', value: 65, color: '#3b82f6' },
  { label: 'Tue', value: 85, color: '#8b5cf6' },
  { label: 'Wed', value: 45, color: '#ec4899' },
  { label: 'Thu', value: 95, color: '#f59e0b' },
  { label: 'Fri', value: 75, color: '#10b981' },
];

export const DataDrivenCharts: React.FC<DataDrivenChartsProps> = ({
  title = '数据驱动的动态图表',
  subtitle = '商业数据可视化',
  data = defaultData,
  background = 'linear-gradient(135deg, #1e3a8a 0%, #312e81 50%, #4c1d95 100%)',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // 图表动画进度
  const chartProgress = interpolate(frame, [30, 170], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // Spring 物理进入动画
  const titleSpring = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
      mass: 0.5,
    },
  });

  // Spring 描述进入动画
  const descSpring = spring({
    frame: frame - 15,
    fps,
    config: {
      damping: 20,
      stiffness: 80,
    },
  });

  // 图表区域尺寸
  const chartWidth = width * 0.7;
  const chartHeight = height * 0.5;
  const chartX = (width - chartWidth) / 2;
  const chartY = height * 0.3;

  // 柱状图参数
  const barWidth = chartWidth / data.length - 40;
  const maxValue = Math.max(...data.map((d) => d.value));

  // 数字滚动效果
  const NumberCounter: React.FC<{ value: number; delay: number; x: number; y: number }> = ({
    value,
    delay,
    x,
    y,
  }) => {
    const progress = interpolate(frame, [delay, delay + 30], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });

    const displayValue = Math.round(value * progress);

    return (
      <div
        style={{
          position: 'absolute',
          left: x,
          top: y,
          fontSize: 28,
          fontWeight: 'bold',
          color: '#fff',
          opacity: progress,
        }}
      >
        {displayValue}
      </div>
    );
  };

  return (
    <AbsoluteFill
      style={{
        background,
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* 标题 */}
      <h1
        style={{
          position: 'absolute',
          top: 60,
          left: '50%',
          transform: `translateX(-50%) translateY(${interpolate(
            titleSpring,
            [0, 1],
            [50, 0]
          )}px)`,
          fontSize: 64,
          fontWeight: 'bold',
          color: '#fff',
          margin: 0,
          textShadow: '0 0 30px rgba(59, 130, 246, 0.8)',
          opacity: titleSpring,
        }}
      >
        {title}
      </h1>

      {/* 描述 */}
      <p
        style={{
          position: 'absolute',
          top: 170,
          left: '50%',
          transform: `translateX(-50%) translateY(${interpolate(
            descSpring,
            [0, 1],
            [30, 0]
          )}px)`,
          fontSize: 32,
          color: 'rgba(255, 255, 255, 0.9)',
          margin: 0,
          opacity: descSpring,
        }}
      >
        {subtitle}
      </p>

      {/* 图表区域 */}
      <div
        style={{
          position: 'absolute',
          left: chartX,
          top: chartY,
          width: chartWidth,
          height: chartHeight,
        }}
      >
        {/* Y 轴 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: 2,
            height: chartHeight,
            background: 'rgba(255, 255, 255, 0.3)',
          }}
        />

        {/* X 轴 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: chartWidth,
            height: 2,
            background: 'rgba(255, 255, 255, 0.3)',
          }}
        />

        {/* 网格线 */}
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: 0,
              top: chartHeight * (1 - (i + 1) / 5),
              width: chartWidth,
              height: 1,
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          />
        ))}

        {/* Y 轴刻度 */}
        {[0, 1, 2, 3, 4].map((i) => {
          const value = (i + 1) * 20;
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: -55,
                top: chartHeight * (1 - (i + 1) / 5) - 14,
                fontSize: 28,
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              {value}
            </div>
          );
        })}

        {/* 柱状图 */}
        {data.map((d, i) => {
          const barHeight = (d.value / maxValue) * chartHeight;
          const x = (chartWidth / data.length) * i + 20;
          const delay = 60 + i * 15;

          // 柱状图高度动画
          const heightProgress = interpolate(frame, [delay, delay + 40], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });

          const currentHeight = barHeight * heightProgress;

          // 柱状图透明度动画
          const opacity = interpolate(frame, [delay, delay + 20], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });

          return (
            <div key={i}>
              {/* 柱子 */}
              <div
                style={{
                  position: 'absolute',
                  left: x,
                  bottom: 20,
                  width: barWidth,
                  height: currentHeight,
                  background: `linear-gradient(to top, ${d.color}, ${d.color}dd)`,
                  borderRadius: '8px 8px 0 0',
                  boxShadow: `0 0 20px ${d.color}66`,
                  opacity,
                }}
              />

              {/* X 轴标签 */}
              <div
                style={{
                  position: 'absolute',
                  left: x + barWidth / 2 - 25,
                  bottom: -42,
                  fontSize: 28,
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.8)',
                  opacity,
                }}
              >
                {d.label}
              </div>

              {/* 数值显示 */}
              <NumberCounter value={d.value} delay={delay + 20} x={x + barWidth / 2 - 15} y={chartY + chartHeight - currentHeight - 150} />
            </div>
          );
        })}
      </div>

      {/* 统计信息面板 */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 60,
          opacity: interpolate(frame, [160, 180], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        {/* 平均值 */}
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.4)',
            padding: '15px 30px',
            borderRadius: 10,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <div style={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.7)' }}>
            平均值
          </div>
          <div style={{ fontSize: 32, fontWeight: 'bold', color: '#fff', marginLeft: 17 }}>
            {Math.round(data.reduce((sum, d) => sum + d.value, 0) / data.length)}
          </div>
        </div>

        {/* 最大值 */}
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.4)',
            padding: '15px 30px',
            borderRadius: 10,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <div style={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.7)' }}>
            最大值
          </div>
          <div style={{ fontSize: 32, fontWeight: 'bold', color: '#f59e0b', marginLeft: 17 }}>
            {maxValue}
          </div>
        </div>

        {/* 最小值 */}
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.4)',
            padding: '15px 30px',
            borderRadius: 10,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <div style={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.7)' }}>
            最小值
          </div>
          <div style={{ fontSize: 32, fontWeight: 'bold', color: '#10b981', marginLeft: 17 }}>
            {Math.min(...data.map((d) => d.value))}
          </div>
        </div>
      </div>

      {/* 进度指示器 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          right: 40,
          background: 'rgba(0, 0, 0, 0.6)',
          padding: '15px 25px',
          borderRadius: 10,
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.7)' }}>
          图表进度
        </div>
        <div style={{ fontSize: 24, color: '#fff', fontWeight: 'bold' }}>
          {Math.round(chartProgress * 100)}%
        </div>
      </div>
    </AbsoluteFill>
  );
};
